import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchStripeConfig,
  saveStripeKeys,
  removeStripeKeys,
  testStripeConnection,
  registerStripeWebhook,
} from "../../../api/integrations";

// Admin-only read: the caller gates on role, and the flag keeps a
// non-admin mount from ever issuing the request.
export function useStripeConfig(enabled: boolean) {
  return useQuery({
    queryKey: ["integrations", "stripe"],
    queryFn: fetchStripeConfig,
    staleTime: 60_000,
    enabled,
  });
}

// A key change flips checkout availability, so everything that renders it
// refreshes: this card, the connections aggregate and the billing screens.
// The mutation response itself is not cached - it may carry a one-time
// mode-swap warning that must not outlive the save that earned it.
function useStripeKeysInvalidation() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["integrations", "stripe"] }),
      queryClient.invalidateQueries({ queryKey: ["integrations", "status"] }),
      queryClient.invalidateQueries({ queryKey: ["billing"] }),
    ]);
  };
}

// Every write shares one mutation key so the surface holding the form can
// ask "is a Stripe call in flight?" without owning the mutations - each
// answer here is one-time (a mode-swap warning, a probe verdict) and dies
// with the component that requested it.
const STRIPE_WRITE = ["integrations", "stripe"];

export function useSaveStripeKeys() {
  const invalidate = useStripeKeysInvalidation();
  return useMutation({
    mutationKey: STRIPE_WRITE,
    mutationFn: saveStripeKeys,
    onSuccess: invalidate,
  });
}

export function useRemoveStripeKeys() {
  const invalidate = useStripeKeysInvalidation();
  return useMutation({
    mutationKey: STRIPE_WRITE,
    mutationFn: removeStripeKeys,
    onSuccess: invalidate,
  });
}

// Read-only probe: nothing to invalidate.
export function useTestStripeConnection() {
  return useMutation({ mutationKey: STRIPE_WRITE, mutationFn: testStripeConnection });
}

export function useRegisterStripeWebhook() {
  const invalidate = useStripeKeysInvalidation();
  return useMutation({
    mutationKey: STRIPE_WRITE,
    mutationFn: registerStripeWebhook,
    onSuccess: invalidate,
  });
}
